import React, { useState, useEffect } from "react";
import axios from "axios";
import { BASE_URL } from "../api";

function ThankYouCompanyRegistration() {
  const [isSending, setIsSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState(""); // "success" or "error"
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isChangingEmail, setIsChangingEmail] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // Countdown timer effect
  useEffect(() => {
    let interval;
    if (countdown > 0) {
      interval = setInterval(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [countdown]);

  const handleSendVerificationEmail = async () => {
    setIsSending(true);
    setMessage("");

    try {
      const token = localStorage.getItem("token");
      const companyId = localStorage.getItem("company_id");

      if (!companyId) {
        setMessage("Company ID not found. Please register again.");
        setMessageType("error");
        setIsSending(false);
        return;
      }

      const response = await axios.post(
       BASE_URL+'company/sendVerificationEmail',
        { companyId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.code == 1) {
        setMessage("Verification email sent successfully! Please check your inbox.");
        setMessageType("success");
        setEmailSent(true);
        setCountdown(60); // Start 60 second countdown
      } else {
        setMessage(response.data.message || "Failed to send verification email.");
        setMessageType("error");
      }
    } catch (error) {
      console.error("Error sending verification email:", error);
      setMessage("An error occurred while sending the verification email.");
      setMessageType("error");
    } finally {
      setIsSending(false);
    }
  };

  const handleResendVerificationEmail = async () => {
    await handleSendVerificationEmail();
  };

  const handleChangeEmail = () => {
    setShowEmailModal(true);
    setNewEmail("");
    setEmailError("");
  };

  const handleSaveEmail = async () => {
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!newEmail.trim()) {
      setEmailError("Email is required");
      return;
    }

    if (!emailRegex.test(newEmail)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    setIsChangingEmail(true);
    setEmailError("");

    try {
      const token = localStorage.getItem("token");
      const companyId = localStorage.getItem("company_id");

      const response = await axios.post(
        BASE_URL+'company/changeEmail',
        { companyId, newEmail },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.code == 1) {
        setMessage("Email updated successfully. Verification email sent to your new email address.");
        setMessageType("success");
        setShowEmailModal(false);
        setNewEmail("");
        setEmailSent(true);
      } else {
        setEmailError(response.data.message || "Failed to update email.");
      }
    } catch (error) {
      console.error("Error changing email:", error);
      setEmailError("An error occurred while updating email. Please try again.");
    } finally {
      setIsChangingEmail(false);
    }
  };

  const handleCloseModal = () => {
    setShowEmailModal(false);
    setNewEmail("");
    setEmailError("");
  };
  return (
    <div
      className="company-form-container mx-auto mt-8 md:mt-16 px-4 md:px-8 py-8 md:py-10 shadow-xl rounded-2xl border border-gray-200"
      style={{
        maxWidth: "600px",
        margin: "2rem auto",
        padding: "1.5rem 1rem 1.5rem 1rem",
        background: "#e8f3f9",
        boxShadow: "0 0 5px rgba(0, 0, 0, 0.1)"
      }}
    >
      <div className="text-center">
        <img
          src="/assets/img/order-summary.png"
          alt="Order Summary"
          style={{ width: '120px', height: '68px', maxWidth: '100%' }}
          className="d-none mx-auto mb-2"
        />
        <h2 className="text-xl md:text-2xl font-semibold text-black mb-3">
          Verify Email
        </h2>
        <p className="text-gray-600 text-sm md:text-base mb-4">
        To ensure the security of your account and move forward with your application, we need to verify your email address.
        </p>
      </div>

      <div className="text-gray-600 space-y-3 leading-relaxed text-sm md:text-base">

        <p className="text-center">
          Please click the <strong>"Verify Email Address"</strong> button to verify your email address and complete your application submission.
        </p>

        <div className="mt-6 text-center">
          {!emailSent ? (
            <button
              onClick={handleSendVerificationEmail}
              disabled={isSending}
              className="btn btn-primary"
              style={{
                backgroundColor: "#007bff",
                color: "white",
                padding: "12px 30px",
                fontSize: "16px",
                fontWeight: "600",
                borderRadius: "8px",
                border: "none",
                cursor: isSending ? "not-allowed" : "pointer",
                minWidth: "250px",
                transition: "all 0.3s ease",
                boxShadow: "0 4px 6px rgba(0, 123, 255, 0.2)"
              }}
              onMouseEnter={(e) => {
                if (!isSending) {
                  e.target.style.backgroundColor = "#0056b3";
                  e.target.style.boxShadow = "0 6px 8px rgba(0, 123, 255, 0.3)";
                }
              }}
              onMouseLeave={(e) => {
                if (!isSending) {
                  e.target.style.backgroundColor = "#007bff";
                  e.target.style.boxShadow = "0 4px 6px rgba(0, 123, 255, 0.2)";
                }
              }}
            >
              {isSending ? "Sending..." : "Verify Email Address"}
            </button>
          ) : (
            <div className="space-y-3">
              <p className="text-gray-600 text-sm">
                Haven't received the email?{" "}
                <a
                  onClick={countdown > 0 ? undefined : handleResendVerificationEmail}
                  disabled={isSending || countdown > 0}
                  style={{
                    color: countdown > 0 ? "#999" : "#007bff",
                    textDecoration: "none",
                    fontWeight: "600",
                    cursor: countdown > 0 ? "not-allowed" : "pointer"
                  }}
                >
                  {isSending ? "Sending..." : countdown > 0 ? `Resend (${countdown}s)` : "Resend"}
                </a>
              </p>
              <p className="d-none text-gray-600 text-sm">
                <a
                  onClick={handleChangeEmail}
                  style={{
                    color: "#007bff",
                    textDecoration: "none",
                    fontWeight: "600",
                    cursor: "pointer"
                  }}
                >
                  Change Email Address
                </a>
              </p>
            </div>
          )}
        </div>

         {message && (
          <div
            className="mt-4 p-3 rounded-lg text-center"
            style={{
              backgroundColor: messageType === "success" ? "#d1fae5" : "#fee2e2",
              color: messageType === "success" ? "#065f46" : "#991b1b"
            }}
          >
            {message}
          </div>
        )}

      {/* Email Change Modal */}
      {showEmailModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '2rem',
              borderRadius: '12px',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}
          >
            <h3
              style={{
                fontSize: '1.25rem',
                fontWeight: '600',
                marginBottom: '1rem',
                color: '#1f2937'
              }}
            >
              Change Email Address
            </h3>
            <div style={{ marginBottom: '1rem' }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: '500',
                  color: '#374151'
                }}
              >
                New Email Address
              </label>
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="Enter new email address"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
              {emailError && (
                <p
                  style={{
                    color: '#dc2626',
                    fontSize: '0.875rem',
                    marginTop: '0.5rem'
                  }}
                >
                  {emailError}
                </p>
              )}
            </div>
            <div
              style={{
                display: 'flex',
                gap: '1rem',
                justifyContent: 'flex-end'
              }}
            >
              <button
                onClick={handleCloseModal}
                disabled={isChangingEmail}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#9ca3af',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: isChangingEmail ? 'not-allowed' : 'pointer',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEmail}
                disabled={isChangingEmail}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: isChangingEmail ? 'not-allowed' : 'pointer',
                  fontWeight: '500'
                }}
              >
                {isChangingEmail ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

export default ThankYouCompanyRegistration;
